import React, { lazy } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
// A calm, static replacement for the scroll-driven integration sphere on the
// home page. A "Giga plugs into your world" eyebrow sits above a headline
// framing the breadth of tools Giga connects to, over a faint network
// backdrop, then a continuous marquee of app-logo tiles runs beneath, capped
// by the CTA. (The "Giga does the work you can't get to" positioning now lives
// in its own dedicated WorkYouCantGetTo section.)
type Logo = {
  slug: string;
  name: string;
};
// App logos rendered as uniform white silhouettes on dark tiles — a single,
// cohesive monochrome treatment so the palette stays tight (dark stage, white
// marks, orange accents) rather than a clash of brand colors. Every slug is a
// canonical simpleicons identifier. Slack is intentionally omitted to keep the
// set on-brand for the monochrome look.
const LOGOS: Logo[] = [{
  slug: 'notion',
  name: 'Notion'
}, {
  slug: 'salesforce',
  name: 'Salesforce'
}, {
  slug: 'hubspot',
  name: 'HubSpot'
}, {
  slug: 'figma',
  name: 'Figma'
}, {
  slug: 'zapier',
  name: 'Zapier'
}, {
  slug: 'shopify',
  name: 'Shopify'
}, {
  slug: 'zoom',
  name: 'Zoom'
}, {
  slug: 'gmail',
  name: 'Gmail'
}, {
  slug: 'googledrive',
  name: 'Google Drive'
}, {
  slug: 'googlecalendar',
  name: 'Google Calendar'
}, {
  slug: 'linear',
  name: 'Linear'
}, {
  slug: 'stripe',
  name: 'Stripe'
}, {
  slug: 'asana',
  name: 'Asana'
}, {
  slug: 'airtable',
  name: 'Airtable'
}, {
  slug: 'calendly',
  name: 'Calendly'
}, {
  slug: 'intercom',
  name: 'Intercom'
}];
function LogoTile({
  logo
}: {
  logo: Logo;
}) {
  return <div className="group/tile relative shrink-0 flex items-center justify-center w-[80px] h-[80px] md:w-[100px] md:h-[100px] rounded-2xl md:rounded-[24px] ring-1 ring-white/[0.08] shadow-[0_10px_30px_-12px_rgba(0,0,0,0.6),inset_0_1px_0_rgba(255,255,255,0.07)] transition-all duration-300 hover:scale-[1.06] hover:ring-white/20" style={{
    backgroundImage: 'linear-gradient(155deg, #2B2018 0%, #211810 55%, #1C140D 100%)'
  }} title={logo.name} data-id="element-2825">
      {/* Soft top-edge highlight — gives the dark tile a little dimension. */}
      <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-2xl md:rounded-[24px] opacity-60" style={{
      backgroundImage: 'linear-gradient(160deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 45%)'
    }} data-id="element-2826" />
      <img src={`https://cdn.simpleicons.org/${logo.slug}`} alt={logo.name}
    // Force every mark to a uniform off-white silhouette regardless of the
    // source icon's native color, so the row reads as one cohesive set.
    className="relative z-10 w-8 h-8 md:w-10 md:h-10 object-contain opacity-90 [filter:brightness(0)_invert(0.92)]" loading="lazy" onError={e => {
      const img = e.currentTarget as HTMLImageElement;
      if (!img.dataset.fallback) {
        img.dataset.fallback = '1';
        img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${logo.slug}.svg`;
      } else {
        img.style.visibility = 'hidden';
      }
    }} data-id="element-2827" />
    </div>;
}
function LogoMarquee({
  reducedMotion
}: {
  reducedMotion: boolean;
}) {
  const row1 = LOGOS.slice(0, 8);
  const row2 = LOGOS.slice(8, 16);
  return <div className="relative w-full overflow-hidden flex flex-col gap-4 md:gap-6" style={{
    WebkitMaskImage: 'linear-gradient(to right, transparent 0, #000 15%, #000 85%, transparent 100%)',
    maskImage: 'linear-gradient(to right, transparent 0, #000 15%, #000 85%, transparent 100%)'
  }} data-id="element-2828">
      <motion.div className="flex gap-4 md:gap-6 w-max" animate={reducedMotion ? undefined : {
      x: ['0%', '-50%']
    }} transition={reducedMotion ? undefined : {
      duration: 50,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-2829">
        {[...row1, ...row1, ...row1, ...row1].map((logo, i) => <LogoTile key={`r1-${logo.slug}-${i}`} logo={logo} data-id="element-2830" />)}
      </motion.div>
      <motion.div className="flex gap-4 md:gap-6 w-max" animate={reducedMotion ? undefined : {
      x: ['-50%', '0%']
    }} transition={reducedMotion ? undefined : {
      duration: 60,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-2831">
        {[...row2, ...row2, ...row2, ...row2].map((logo, i) => <LogoTile key={`r2-${logo.slug}-${i}`} logo={logo} data-id="element-2832" />)}
      </motion.div>
    </div>;
}
// Faint constellation backdrop — thin connector lines + soft dots, kept very
// low-contrast so it reads as texture behind the headline, not noise.
function NetworkBackdrop() {
  return <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true" data-id="element-2833">
      {/* Very soft warm glow centered behind the headline — fully transparent
                                                                                    well before the section edges so it never creates a visible band. */}
      <div className="absolute inset-0" style={{
      background: 'radial-gradient(60% 55% at 50% 32%, rgba(234,88,12,0.05) 0%, transparent 70%)'
    }} data-id="element-2834" />
      {/* Subtle dot grid — light dots so they read on the dark-brown stage */}
      <div className="absolute inset-0" style={{
      backgroundImage: 'radial-gradient(rgba(255,255,255,0.07) 1px, transparent 1px)',
      backgroundSize: '32px 32px',
      maskImage: 'radial-gradient(100% 100% at 50% 30%, #000 20%, transparent 80%)',
      WebkitMaskImage: 'radial-gradient(100% 100% at 50% 30%, #000 20%, transparent 80%)'
    }} data-id="element-2835" />
    </div>;
}
export function StaticConnectionsBeat() {
  const reducedMotion = useReducedMotion();
  return <section className="relative w-full overflow-hidden bg-[#1A1410] py-24 md:py-36" data-id="element-2836">
      <NetworkBackdrop data-id="element-2837" />

      <div className="relative z-10 mx-auto flex max-w-[1280px] flex-col items-center px-6 text-center md:px-10" data-id="element-2838">
        <h2 className="max-w-4xl text-3xl md:text-5xl font-title font-medium leading-[1.1] tracking-tight text-[#FAF8F4]" data-id="element-2839">
          Shared intelligence for your stack.
        </h2>
        <p className="mt-5 max-w-2xl text-lg leading-relaxed text-white/65 md:mt-6 md:text-xl" data-id="element-2840">
          Giga understands your data and takes action on your behalf, with you
          in control.
        </p>
      </div>

      <div className="relative z-10 mx-auto mt-14 w-full max-w-6xl md:mt-20" data-id="element-2841">
        <LogoMarquee reducedMotion={!!reducedMotion} data-id="element-2842" />
      </div>

      <div className="relative z-10 mt-9 flex justify-center px-6 md:px-10 md:mt-11" data-id="element-2843">
        <button className="group inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-semibold text-black shadow-lg shadow-black/20 transition-all duration-300 hover:bg-neutral-100 hover:scale-105" data-id="element-2844">
          See all integrations
          <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5" data-id="element-2845" />
        </button>
      </div>
    </section>;
}